// MobileSheet — the design's `.mqr-backdrop` + `.mqr-sheet` bottom sheet.
// Blurred gradient backdrop, slide-up sheet anchored to the bottom with a
// grabber. `fill` switches to the catalog-browser variant (`.mcb-sheet`):
// fixed full height with an internally-scrolling body.

import { Box, Portal } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useEffect, useState, type ReactNode } from "react";

const fadeIn = keyframes`from { opacity: 0 } to { opacity: 1 }`;
const fadeOut = keyframes`from { opacity: 1 } to { opacity: 0 }`;
const slideUp = keyframes`from { transform: translateY(100%) } to { transform: translateY(0) }`;
const slideDown = keyframes`from { transform: translateY(0) } to { transform: translateY(100%) }`;

interface MobileSheetProps {
  isOpen: boolean;
  onClose: () => void;
  label: string;
  /** Catalog-browser variant: fixed full height, body scrolls internally. */
  fill?: boolean;
  children: ReactNode;
}

const MobileSheet = ({
  isOpen,
  onClose,
  label,
  fill,
  children,
}: MobileSheetProps) => {
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const requestClose = () => {
    if (closing) return;
    setClosing(true);
    window.setTimeout(() => {
      setClosing(false);
      onClose();
    }, 220);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, closing]);

  if (!isOpen) return null;

  return (
    <Portal>
      <Box
        position="fixed"
        inset={0}
        zIndex={1500}
        display="flex"
        alignItems="flex-end"
        justifyContent="center"
        onClick={requestClose}
        animation={`${closing ? fadeOut : fadeIn} 200ms ease-out`}
        sx={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.30) 100%)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        }}
      >
        <Box
          role="dialog"
          aria-modal="true"
          aria-label={label}
          onClick={(e) => e.stopPropagation()}
          w="full"
          maxW="480px"
          bg="neutral.25"
          borderTopRadius="20px"
          boxShadow="0 -20px 60px rgba(16,24,40,0.25)"
          display="flex"
          flexDirection="column"
          // iOS-style drawer slide. Reduced-motion users get a plain fade
          // (opacity aids comprehension; the vertical travel is what we drop).
          animation={`${closing ? slideDown : slideUp} 320ms cubic-bezier(0.32, 0.72, 0, 1)`}
          sx={{
            "@media (prefers-reduced-motion: reduce)": {
              animation: `${closing ? fadeOut : fadeIn} 200ms ease-out !important`,
            },
          }}
          px="20px"
          pt="8px"
          {...(fill
            ? {
                h: "calc(100dvh - 16px)",
                maxH: "calc(100dvh - 16px)",
                overflow: "hidden",
                gap: "12px",
                pb: "calc(20px + env(safe-area-inset-bottom))",
              }
            : {
                maxH: "calc(100dvh - 24px)",
                overflowY: "auto",
                gap: "14px",
                pb: "calc(24px + env(safe-area-inset-bottom))",
              })}
        >
          {/* grabber */}
          <Box
            w="36px"
            h="4px"
            borderRadius="full"
            bg="neutral.300"
            m="4px auto"
            flexShrink={0}
          />
          {children}
        </Box>
      </Box>
    </Portal>
  );
};

export default MobileSheet;
